import React, { MouseEventHandler, useContext } from 'react'
import { NavDropdown } from 'react-bootstrap'
import { AuthContext } from 'app/modules/auth'
import style from './user-button.module.scss'
import { applicationConfiguration } from '../../../modules/shared'
import { doSignout } from '../../../modules/auth/helper/signout'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { SigninLink } from 'app/modules/auth/components/auth-link'
import { useLocation } from 'react-router'
import AppbarDropdown from '../appbar-dropdown'
import { GetActiveClassFunc, getActiveRouteClass, getActiveSubRouteClass } from '../helpers/helpers'
import { AuthContextData } from 'app/modules/auth/context'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { getPageUrl } from 'app/modules/shared/routing'

export const UserButton = (): JSX.Element => {
  const { _t } = useTypedTranslation()
  const config = useContext(applicationConfiguration)
  const auth = useContext(AuthContext)
  const user = auth.user
  const currentRoute = useLocation().pathname.substring(1)
  const getActiveMenuClass = (activeRoutes: string[], getActiveClass: GetActiveClassFunc) =>
    getActiveClass(activeRoutes, currentRoute, 'appbar-dropdown-item-selected')
  const activeRouteClass = getActiveSubRouteClass(['users', 'me'], currentRoute, 'appbar-dropdown-selected')
  const activeSignInClass = getActiveSubRouteClass(['auth'], currentRoute, 'appbar-item-selected')

  const handleSignout = () => {
    doSignout(auth, config)
  }

  if (!user) {
    return <SigninLink className={`appbar-item with nav-link ${activeSignInClass}`} />
  }

  return (
    <AppbarDropdown
      className={`${activeRouteClass} appbar-user-menu`}
      title={<i className={`ds3 ds3-user __${style.userIcon}`} />}
    >
      <UserMenu auth={auth} getActiveMenuClass={getActiveMenuClass} t={_t as TFunction} handleSignout={handleSignout} />
    </AppbarDropdown>
  )
}

function UserMenu({
  auth,
  getActiveMenuClass,
  t,
  handleSignout,
}: {
  auth: AuthContextData
  getActiveMenuClass: CallableFunction
  t: TFunction
  handleSignout: MouseEventHandler<HTMLElement>
}) {
  const { _t } = useTypedTranslation()
  const user = auth?.user
  const isAdmin = user?.isAdmin || user?.isAuditor
  const name = user?.username || user?.email

  return (
    <div className="appbar-user-dropdown">
      <NavDropdown.Header className="py-3 px-4" aria-label="Profile name">
        <div className="text-body text-m ellipsis">{name}</div>
        {user?.externalAuth ? (
          <div className="text-body text-m ellipsis" title={t('external-user-id')}>
            {user.externalAuth.service}: {user.externalAuth.userId}
          </div>
        ) : null}
      </NavDropdown.Header>
      <NavDropdown.Divider className="my-0" />
      <div className="appbar-user-dropdown-body">
        <Link
          className={`dropdown-item ${getActiveMenuClass(['users/profile'], getActiveRouteClass)}`}
          to={getPageUrl('user-profile') as string}
        >
          {_t('my-settings')}
        </Link>
        <Link
          className={`dropdown-item ${getActiveMenuClass(['me/reports'], getActiveRouteClass)}`}
          to={getPageUrl('my-reports') as string}
        >
          {_t('my-reports')}
        </Link>
        <Link
          className={`dropdown-item ${getActiveMenuClass(['me/downloads'], getActiveRouteClass)}`}
          to={getPageUrl('my-downloads') as string}
        >
          {_t('my-downloads')}
        </Link>
        {isAdmin ? (
          <Link
            className={`dropdown-item ${getActiveMenuClass(['admin'], getActiveSubRouteClass)}`}
            to={getPageUrl('admin-home') as string}
          >
            {_t('admin-panel')}
          </Link>
        ) : null}
      </div>
      <NavDropdown.Divider className="mt-0 mb-2 hide-mobile" />
      <NavDropdown.Item className="dropdown-item" onClick={handleSignout}>
        {t('sign-out')}
      </NavDropdown.Item>
    </div>
  )
}
